import { useEffect, useState } from 'react'
import { InsuredObjectsOutput, Referral } from '../@types/RunPetisSimulation'
import { useSimulatorStore } from '../Stores/simulatorStore'
import { useRunPetisSimulationStore } from '../Stores/useRunPetisSimulationStore'
import { useProgressStore } from '../Stores/progressStore'
import { REFERRALS } from '../constants/referrals'

export type _InsuredObjectsType = {
  insuredObject: Pick<InsuredObjectsOutput, 'insuredObjectId' | 'insuredPet'>
}

export const useReferrals = () => {
  const [disabledFullStop, setDisabledFullStop] = useState(false)
  const [referralsStop, setReferralsStop] = useState<Referral[]>()
  const { runPetisSimulation, setIsReferral } = useRunPetisSimulationStore()
  const { currentPack, setIsFullStop } = useSimulatorStore()
  const { currentStep } = useProgressStore()

  const handleDisableReportSimplified = () => {
    const allPacks = Array.from(
      new Set(
        runPetisSimulation?.runPetisSimulationResponse?.insuredObjectGroup?.insuredObjects
          ?.map((animal) =>
            animal.packsObject?.packs
              ?.map((pack) => (pack.premiums ? pack.packId : null))
              .filter(Boolean),
          )
          .flatMap((pack) => pack || []),
      ),
    )

    return allPacks
  }

  const handleReferralsMessages = () => {
    const referrals =
      runPetisSimulation?.runPetisSimulationResponse?.referrals || []
    const messagesPacks: { outcomeMessage: string; packIds: string[] }[] = []
    const insuredObject =
      runPetisSimulation?.runPetisSimulationResponse?.insuredObjectGroup
        ?.insuredObjects?.[0]
    const packsOrdered = insuredObject?.packsObject?.packs
      ?.sort((a: any, b: any) => a.packOrder - b.packOrder)
      .map((pack) => pack.packId)

    const referralsFormattedMessage = referrals
      .map((referral) => ({
        ...referral,
        outcomeMessage: `${referral.insuredObjectName} - ${referral.outcomeMessage}`,
      }))
      .sort(
        (a: any, b: any) => a.insuredObjectSequence - b.insuredObjectSequence,
      )

    const filterReferralsByOutcome = (outcomeMessage: string) => {
      const filteredReferrals = referralsFormattedMessage.filter(
        (referral) => referral.outcomeMessage === outcomeMessage,
      )

      const packIds = (packsOrdered || [])
        ?.map(
          (pack) =>
            filteredReferrals?.find(
              (referral) =>
                referral.packId === pack &&
                referral.outcomeTypeCode === REFERRALS.fullStop,
            )?.packId,
        )
        .filter(Boolean) as string[]

      return { outcomeMessage, packIds }
    }

    const getUniqueReferrals = (_referrals: Referral[]) => {
      const outcomeMessages: string[] = []
      const items: Referral[] = []

      _referrals.forEach((referral) => {
        if (!outcomeMessages.includes(referral.outcomeMessage!)) {
          outcomeMessages.push(referral.outcomeMessage!)
          messagesPacks.push(filterReferralsByOutcome(referral.outcomeMessage!))
          items.push(referral)
        }
      })

      return items
    }

    return getUniqueReferrals(referralsFormattedMessage).map((item, index) => {
      const packs =
        messagesPacks?.find(
          (message) => message.outcomeMessage === item.outcomeMessage,
        )?.packIds || []

      return {
        type: 'Message',
        attributes: {
          id: index,
          value: `${item.outcomeMessage} ${
            packs?.length > 0 ? `(${packs.join(', ')})` : ''
          }`,
          flag:
            item.outcomeTypeCode === REFERRALS.fullStop
              ? 'ineligible'
              : 'refer',
        },
      }
    })
  }

  useEffect(() => {
    // TODO: CHECK THIS SOLUTION BASED FOR ANIMALS
      const referrals = runPetisSimulation?.runPetisSimulationResponse?.referrals

    if (referrals?.length === 0) {
      setIsReferral(false)
      setIsFullStop(false)
    }

      const disabledReferralsFullStop =
          Object.values(currentPack).length === 0 ||
          Object.values(currentPack).some((packId, arrayPosition) =>
              (referrals || []).some(referral =>
                  referral?.insuredObjectSequence === (arrayPosition + 1) &&
                  referral?.outcomeTypeCode === REFERRALS.fullStop &&
                  referral?.packId === packId
              )
          );

      setDisabledFullStop(disabledReferralsFullStop);

    if (
      runPetisSimulation?.runPetisSimulationResponse?.referrals?.filter(
          (item) => item?.outcomeTypeCode === REFERRALS.fullStop,
      )?.length
    ) {
      setIsFullStop(true)
      if (currentStep > 2) { setIsReferral(false) }
    } else {
      setIsFullStop(false)
    }

    setReferralsStop(
      (referrals || [])?.filter(
        (referral) => referral?.outcomeTypeCode === REFERRALS.fullStop,
      ),
    )
  }, [currentPack, runPetisSimulation])

  return {
    disabledFullStop,
    enabledPacks: handleDisableReportSimplified(),
    referralsStop,
    handleReferralsMessages,
    handleDisableReportSimplified: () =>
      (handleDisableReportSimplified() || [])?.length > 0,
  }
}
